import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  InjectionToken,
  Output,
  effect,
  forwardRef,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  type AbstractControl,
  type ControlValueAccessor,
  type ValidationErrors,
  type Validator,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Money } from '@principle-theorem/accounting';
import { AppointmentSchedulingFacade } from '@principle-theorem/ng-appointment/store';
import { PrincipleEditorModule } from '@principle-theorem/ng-interactions';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import { DEFAULT_CANCELLATION_FEE_ATTRIBUTION } from '@principle-theorem/principle-core';
import {
  CancellationFeeAttribution,
  type IDepositForfeitureDecision,
  type ISchedulingEventReason,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  type DocumentReference,
} from '@principle-theorem/shared';
import { BehaviorSubject, combineLatest, type Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import {
  DepositForfeiture,
  MINIMUM_FORFEIT_AMOUNT_DOLLARS,
  type IDepositForfeitureContext,
  type IFeeAttributionView,
} from './deposit-forfeiture-control';
import {
  PlannedDepositOutcome,
  type IPlannedDepositOutcome,
} from './deposit-forfeiture-outcome';

export const DEPOSIT_FORFEITURE_CONTEXT_RESOLVER = new InjectionToken<
  typeof DepositForfeiture.resolveDepositForfeitureContext
>('DEPOSIT_FORFEITURE_CONTEXT_RESOLVER', {
  factory: () => DepositForfeiture.resolveDepositForfeitureContext,
});

@Component({
  selector: 'pr-deposit-forfeiture-control',
  templateUrl: './deposit-forfeiture-control.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    PrincipleEditorModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatTooltipModule,
    MatSlideToggleModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DepositForfeitureControlComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DepositForfeitureControlComponent),
      multi: true,
    },
  ],
})
export class DepositForfeitureControlComponent
  implements ControlValueAccessor, Validator
{
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _schedulingFacade = inject(AppointmentSchedulingFacade);
  private readonly _resolveContext = inject(
    DEPOSIT_FORFEITURE_CONTEXT_RESOLVER
  );
  private readonly _context$ = new BehaviorSubject<
    IDepositForfeitureContext | undefined
  >(undefined);
  /**
   * Separate from `_context$` because a resolved `undefined` context and a
   * context that has not been looked up yet are both `undefined` there, and
   * only the second must hold the save. Submitting mid-lookup writes a
   * scheduling event with no forfeiture decision, which silently skips the
   * cancellation fee.
   */
  private readonly _resolving$ = new BehaviorSubject<boolean>(true);
  /**
   * Undefined until the first derivation, so that derivation always reaches
   * the page even when it agrees with the page's own default.
   */
  private _actionable?: boolean;
  /**
   * Pushed from `_emitValue`, which runs after both a form edit and a context
   * reset, so it never reads a stale selection or default.
   */
  private readonly _feeAttribution$ = new BehaviorSubject<
    | {
        selected: CancellationFeeAttribution;
        defaultAttribution: CancellationFeeAttribution;
      }
    | undefined
  >(undefined);

  readonly reasonRef = input<DocumentReference<ISchedulingEventReason>>();
  readonly canOverride = input(false);
  @Output() readonly outcomeChanged =
    new EventEmitter<IPlannedDepositOutcome>();
  @Output() readonly resolvingChanged = new EventEmitter<boolean>();
  /**
   * Whether this control is actually on screen and editable. The card's
   * warning copy may only tell staff to use the toggle when this is true —
   * deposit metadata alone cannot know, because a deposit recorded against the
   * appointment with no deposit request behind it resolves no context here.
   */
  @Output() readonly actionableChanged = new EventEmitter<boolean>();
  readonly visible$: Observable<boolean>;
  readonly resolving$: Observable<boolean> = this._resolving$.asObservable();
  readonly form = DepositForfeiture.createDepositForfeitureForm();
  readonly minAmountDollars = MINIMUM_FORFEIT_AMOUNT_DOLLARS;
  /**
   * Drives both the editable line and the read-only sentence shown to staff
   * without the override permission, who still need to know who gets the fee.
   */
  readonly feeAttributionView$: Observable<IFeeAttributionView | undefined>;
  requiresOverrideReason = false;
  amountExceedsDeposit = false;
  /**
   * Mirrors `Validators.max` onto the input's `max` attribute. Undefined until
   * a context resolves, which removes the attribute rather than writing a
   * bogus one — the amount field only renders once a context exists.
   */
  maxAmountDollars?: number;

  constructor() {
    effect(() => {
      const canOverride = this.canOverride();
      this._configureFormAccess(canOverride);
      this._setActionable(canOverride);
    });
    this.visible$ = this._context$.pipe(map(Boolean));
    this.feeAttributionView$ = combineLatest([
      this._feeAttribution$,
      this._schedulingFacade.currentAppointment$,
    ]).pipe(
      map(([attribution, appointment]) =>
        attribution
          ? DepositForfeiture.describeFeeAttribution(
              attribution.selected,
              attribution.defaultAttribution,
              appointment?.practitioner.name
            )
          : undefined
      )
    );
    combineLatest([
      this._schedulingFacade.currentAppointment$.pipe(filterUndefined()),
      this._schedulingFacade.brand$.pipe(filterUndefined()),
      toObservable(this.reasonRef),
    ])
      .pipe(
        tap(() => this._setResolving(true)),
        switchMap(([appointment, brand, reasonRef]) =>
          this._resolveContext(appointment, brand, reasonRef)
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((context) => this._setContext(context));

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._configureValidators();
        this._emitValue();
      });
  }

  writeValue(value?: IDepositForfeitureDecision): void {
    if (!value) {
      return;
    }
    this.form.patchValue(
      {
        shouldForfeit: value.shouldForfeit,
        amountDollars: value.shouldForfeit
          ? Money.fromCents(value.amountCents)
          : undefined,
        ...(value.feeAttribution
          ? { feeAttribution: value.feeAttribution }
          : {}),
        overrideReason: value.overrideReason,
      },
      { emitEvent: false }
    );
  }

  registerOnChange(
    change: (value: IDepositForfeitureDecision | undefined) => void
  ): void {
    this._change = change;
  }

  registerOnTouched(touched: () => void): void {
    this._touched = touched;
  }

  registerOnValidatorChange(validatorChange: () => void): void {
    this._validatorChange = validatorChange;
  }

  setDisabledState(disabled: boolean): void {
    if (disabled) {
      this.form.disable({ emitEvent: false });
      return;
    }
    this.form.enable({ emitEvent: false });
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (this._context$.value && this.form.invalid) {
      return { depositForfeiture: true };
    }
    // eslint-disable-next-line no-null/no-null -- Angular Validator requires null for a valid control.
    return null;
  }

  touched(): void {
    this._touched();
  }

  selectFeeAttribution(attribution: CancellationFeeAttribution): void {
    this.form.controls.feeAttribution.setValue(attribution);
    this._touched();
  }

  private _change: (value: IDepositForfeitureDecision | undefined) => void =
    () => undefined;
  private _touched: () => void = () => undefined;
  private _validatorChange: () => void = () => undefined;

  /**
   * Settles for every lookup outcome, including a context of `undefined`. An
   * appointment with no deposit request has nothing to forfeit and must stay
   * saveable, so a resolved-to-nothing lookup is still resolved.
   */
  private _setContext(context?: IDepositForfeitureContext): void {
    this._context$.next(context);
    this._setResolving(false);
    this.form.reset(
      {
        shouldForfeit: context?.defaultShouldForfeit ?? false,
        amountDollars: context
          ? Money.fromCents(context.defaultAmountCents)
          : undefined,
        feeAttribution:
          context?.defaultFeeAttribution ??
          DEFAULT_CANCELLATION_FEE_ATTRIBUTION,
        overrideReason: undefined,
      },
      { emitEvent: false }
    );
    this._configureValidators();
    this._configureFormAccess(this.canOverride());
    this._setActionable(this.canOverride());
    this._emitValue();
  }

  private _setResolving(resolving: boolean): void {
    if (this._resolving$.value === resolving) {
      return;
    }
    this._resolving$.next(resolving);
    this.resolvingChanged.emit(resolving);
  }

  /**
   * Re-derived from both inputs the control's visibility depends on, so it is
   * called from the `canOverride` effect and from every context resolution.
   * Deduped like `_setResolving`, so a page only hears about real changes.
   */
  private _setActionable(canOverride: boolean): void {
    const actionable = this._context$.value !== undefined && canOverride;
    if (this._actionable === actionable) {
      return;
    }
    this._actionable = actionable;
    this.actionableChanged.emit(actionable);
  }

  private _configureValidators(): void {
    const context = this._context$.value;
    if (
      context &&
      this.form.controls.shouldForfeit.value &&
      this.form.controls.amountDollars.value === undefined
    ) {
      this.form.controls.amountDollars.setValue(
        Money.fromCents(context.defaultAmountCents),
        { emitEvent: false }
      );
    }
    this.maxAmountDollars = context
      ? DepositForfeiture.maximumForfeitAmountDollars(context)
      : undefined;
    this.requiresOverrideReason =
      DepositForfeiture.configureDepositForfeitureValidators(
        this.form,
        context
      );
    this.amountExceedsDeposit =
      this.form.controls.amountDollars.hasError('max');
    this._validatorChange();
  }

  private _emitValue(): void {
    const context = this._context$.value;
    this._emitPlannedOutcome(context);
    this._feeAttribution$.next(
      context
        ? {
            selected:
              this.form.controls.feeAttribution.value ??
              context.defaultFeeAttribution,
            defaultAttribution: context.defaultFeeAttribution,
          }
        : undefined
    );
    if (!context) {
      this._change(undefined);
      this._validatorChange();
      return;
    }
    const decision = DepositForfeiture.toDepositForfeitureDecision(
      this.form.getRawValue(),
      context
    );
    this._change(decision);
    this._validatorChange();
  }

  private _emitPlannedOutcome(context?: IDepositForfeitureContext): void {
    const formValue = this.form.getRawValue();
    this.outcomeChanged.emit(
      PlannedDepositOutcome.fromControlState({
        context,
        shouldForfeit: formValue.shouldForfeit,
        amountDollars: formValue.amountDollars,
      })
    );
  }

  /**
   * Second line of defence behind the template gate: without the override
   * permission the controls are not rendered at all, but the form is disabled
   * too so nothing can be written through the value accessor either.
   */
  private _configureFormAccess(canOverride: boolean): void {
    if (canOverride && this._context$.value) {
      this.form.enable({ emitEvent: false });
      return;
    }
    this.form.disable({ emitEvent: false });
  }
}
